import { useState, useMemo } from 'react';
import { Plus, Pencil, LogOut, Trash2, Users, X } from 'lucide-react';
import KpiCard from '../components/KpiCard.jsx';
import Modal from '../components/Modal.jsx';
import FormField from '../components/FormField.jsx';
import Button from '../components/Button.jsx';
import EmployeeFormModal from '../components/employees/EmployeeFormModal.jsx';
import { useEmployees, useDeleteEmployee, useUpdateEmployee } from '../hooks/useEmployees.js';
import { formatCurrency, formatDate } from '../lib/format.js';

function toLocalISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function fullName(employee) {
  return `${employee.first_name} ${employee.last_name}`.trim();
}

// Même règle que le backend : un employé est sorti quand sa date de sortie est
// déjà passée (une sortie planifiée reste un actif jusqu'au jour dit).
function isActive(employee, todayISO) {
  return !employee.end_date || employee.end_date >= todayISO;
}

const FREQUENCY_SUFFIX = { monthly: '/mois', quarterly: '/trim.' };

const STATUS_OPTIONS = [
  { value: 'active', label: 'Actifs' },
  { value: 'left', label: 'Sortis' },
];

function StatusToggle({ value, onChange }) {
  return (
    <div
      className="flex rounded-lg border border-slate-200 overflow-hidden text-xs w-fit"
      role="group"
      aria-label="Filtre par statut"
    >
      {STATUS_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={value === opt.value}
          className={`px-4 py-2.5 min-h-[44px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
            value === opt.value
              ? 'bg-slate-800 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Dialogue « Marquer la sortie » ────────────────────────────────────────────

function MarkLeavingDialog({ employee, onClose }) {
  const [endDate, setEndDate] = useState(() => employee.end_date ?? toLocalISODate(new Date()));
  const updateMut = useUpdateEmployee();

  async function handleConfirm() {
    try {
      await updateMut.mutateAsync({ id: employee.id, end_date: endDate });
    } catch {
      // Le dialogue reste ouvert : le message est rendu depuis updateMut.error.
      return;
    }
    onClose();
  }

  return (
    <Modal open onClose={onClose} title="Marquer la sortie" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          <strong className="text-slate-800">{fullName(employee)}</strong> — le salaire et ses charges
          cesseront d'être projetés après cette date.
        </p>

        <FormField label="Date de sortie" htmlFor="employee-leave-date" required>
          <input
            id="employee-leave-date"
            type="date"
            value={endDate}
            min={employee.start_date}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition-colors"
          />
        </FormField>

        {updateMut.error && (
          <p className="text-sm text-red-600" role="alert">{updateMut.error.message}</p>
        )}

        <div className="flex gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} disabled={updateMut.isPending} className="flex-1">
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            loading={updateMut.isPending}
            disabled={updateMut.isPending || !endDate}
            className="flex-1"
          >
            Confirmer
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Carte employé ─────────────────────────────────────────────────────────────

function EmployeeCard({ employee, active, onEdit, onMarkLeaving, onDelete }) {
  const name = fullName(employee);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
          {employee.job_title && (
            <p className="text-xs text-slate-500 truncate">{employee.job_title}</p>
          )}
          <p className="text-[11px] text-slate-400 mt-0.5">
            <span>entré le {formatDate(employee.start_date)}</span>
            {employee.end_date && <span> · sorti le {formatDate(employee.end_date)}</span>}
          </p>
        </div>

        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => onEdit(employee)}
            aria-label={`Modifier ${name}`}
            title="Modifier"
            className="inline-flex items-center justify-center p-2 min-h-[44px] min-w-[44px] rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            <Pencil size={16} aria-hidden />
          </button>
          {active && (
            <button
              type="button"
              onClick={() => onMarkLeaving(employee)}
              aria-label={`Marquer la sortie de ${name}`}
              title="Marquer la sortie"
              className="inline-flex items-center justify-center p-2 min-h-[44px] min-w-[44px] rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            >
              <LogOut size={16} aria-hidden />
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(employee)}
            aria-label={`Supprimer ${name}`}
            title="Supprimer"
            className="inline-flex items-center justify-center p-2 min-h-[44px] min-w-[44px] rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
          >
            <Trash2 size={16} aria-hidden />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">Salaire net</span>
        <span className="font-semibold text-slate-800 tabular-nums">
          {formatCurrency(employee.net_salary_cents)}
          <span className="font-normal text-slate-400 ml-0.5">/mois</span>
        </span>
      </div>

      {(employee.charges ?? []).map((charge) => (
        <div key={charge.id} className="flex items-center justify-between text-xs">
          <span className="text-slate-500 truncate">{charge.label}</span>
          <span className="text-slate-600 tabular-nums whitespace-nowrap ml-2">
            {formatCurrency(charge.amount_cents)}
            <span className="text-slate-400 ml-0.5">
              {FREQUENCY_SUFFIX[charge.frequency] ?? '/mois'}
            </span>
          </span>
        </div>
      ))}

      <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-xs">
        <span className="font-medium text-slate-600">Coût mensuel</span>
        <span className="font-semibold text-slate-900 tabular-nums">
          {formatCurrency(employee.monthly_cost_cents)}
        </span>
      </div>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 animate-pulse">
      <div className="h-4 w-1/2 rounded bg-slate-200" />
      <div className="h-3 w-1/3 rounded bg-slate-100" />
      <div className="h-3 w-full rounded bg-slate-100" />
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function EmployeesPage() {
  const [statusFilter, setStatusFilter] = useState('active');
  const [formModal, setFormModal] = useState(null); // null | { employee: object|null }
  const [leavingTarget, setLeavingTarget] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Un seul fetch : les KPIs portent sur les actifs quel que soit l'onglet
  // affiché, et le volume (quelques fiches) ne justifie pas un refetch par filtre.
  const { data, isLoading, error, refetch } = useEmployees({ status: 'all' });
  const deleteMut = useDeleteEmployee();

  const employees = Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);
  const todayISO = toLocalISODate(new Date());

  const activeEmployees = useMemo(
    () => employees.filter((e) => isActive(e, todayISO)),
    [employees, todayISO]
  );

  const monthlyCost = activeEmployees.reduce((sum, e) => sum + (e.monthly_cost_cents ?? 0), 0);

  const visible = statusFilter === 'active'
    ? activeEmployees
    : employees.filter((e) => !isActive(e, todayISO));

  function openCreate() {
    setFormModal({ employee: null });
  }

  async function handleDelete(employee) {
    if (!window.confirm(`Supprimer la fiche de ${fullName(employee)} ? Ses prévisionnels seront retirés.`)) return;
    setActionError(null);
    try {
      await deleteMut.mutateAsync(employee.id);
    } catch (err) {
      setActionError(
        `Suppression impossible : ${err?.message ?? 'une erreur est survenue'}`
      );
    }
  }

  const isEmpty = !isLoading && !error && employees.length === 0;

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-24 lg:pb-6">
      <div className="flex items-center gap-3">
        <h1 className="flex-1 text-xl font-bold text-slate-900">Employés</h1>
        <button
          type="button"
          onClick={openCreate}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        >
          <Plus size={15} aria-hidden /> Ajouter
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3" data-testid="employees-kpis">
        <KpiCard
          label="Coût mensuel"
          value={formatCurrency(monthlyCost)}
          sub="salaires nets + charges"
          loading={isLoading}
        />
        <KpiCard
          label="Effectif"
          value={String(activeEmployees.length)}
          sub="employés actifs"
          icon={Users}
          loading={isLoading}
        />
      </div>

      <StatusToggle value={statusFilter} onChange={setStatusFilter} />

      {actionError && (
        <div
          className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          <span className="flex-1">{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            aria-label="Masquer l'erreur"
            className="shrink-0 rounded p-1 text-red-400 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
          >
            <X size={14} aria-hidden />
          </button>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      )}

      {error && !isLoading && (
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-8 text-center space-y-3">
          <p className="text-sm text-red-600">Impossible de charger les employés.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-sm font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900"
          >
            Réessayer
          </button>
        </div>
      )}

      {isEmpty && (
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-10 text-center space-y-3">
          <p className="text-sm text-slate-500">
            Aucun employé enregistré. Les salaires et charges saisis ici alimentent
            automatiquement les prévisionnels.
          </p>
          <Button type="button" onClick={openCreate}>
            <Plus size={15} aria-hidden /> Ajouter un employé
          </Button>
        </div>
      )}

      {!isLoading && !error && !isEmpty && visible.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-10 text-center">
          <p className="text-sm text-slate-500">
            {statusFilter === 'active' ? 'Aucun employé actif.' : 'Aucun employé sorti.'}
          </p>
        </div>
      )}

      {!isLoading && !error && visible.length > 0 && (
        <div className="space-y-3">
          {visible.map((employee) => (
            <EmployeeCard
              key={employee.id}
              employee={employee}
              active={statusFilter === 'active'}
              onEdit={(e) => setFormModal({ employee: e })}
              onMarkLeaving={setLeavingTarget}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {formModal && (
        <EmployeeFormModal open onClose={() => setFormModal(null)} employee={formModal.employee} />
      )}
      {leavingTarget && (
        <MarkLeavingDialog employee={leavingTarget} onClose={() => setLeavingTarget(null)} />
      )}
    </div>
  );
}
